import { SITE_ORIGIN, CATEGORIES } from 'constants/site';
import {
  AUTHOR_REGEX,
  COLLECTION_REGEX,
  POST_REGEX,
  SEARCH_REGEX,
  VIDEO_ID_REGEX,
  VIDEO_URL_REGEX,
  YOUTUBE_REGEX,
  IMAGE_COLLECTION_REGEX
} from 'constants/regex';
import { filterTermsBySlug } from './terms';

const INTERNAL_LINK_REGEX = new RegExp(`^${SITE_ORIGIN}`, 'i');

export const isInternalLink = url => INTERNAL_LINK_REGEX.test(url);

export const isExternalLink = url => INTERNAL_LINK_REGEX.test(url) === false;

export const isVideoLink = url =>
  VIDEO_ID_REGEX.test(url) || VIDEO_URL_REGEX.test(url);

export const isYoutubeLink = url => YOUTUBE_REGEX.test(url);

export const toUrl = url => {
  try {
    // Add protocol to protocol relative URLs
    const fullUrl = url.replace(/^\/\//, 'https://');
    // Attempt to parse
    return new URL(fullUrl, SITE_ORIGIN);
  } catch (e) {
    return undefined;
  }
};

export const getLinkProps = url => {
  // Convert to URL object
  const parsed = toUrl(url);

  let props = {
    href: url
  };

  // Internal URL?
  if (parsed && parsed.host === toUrl(SITE_ORIGIN).host) {
    const { pathname, search } = parsed;
    const as = `${pathname}${search}`;

    if (POST_REGEX.test(pathname)) {
      // Post URL
      const [, id] = pathname.match(POST_REGEX);
      props = {
        href: `/post/show?id=${id}`,
        as
      };
    } else if (CATEGORIES.find(c => c.as === pathname)) {
      // Category URL
      props = {
        href: `/categories/show?category=${pathname.slice(1)}`,
        as
      };
    } else if (COLLECTION_REGEX.test(pathname)) {
      // Collection URL
      const [, tag] = pathname.match(COLLECTION_REGEX);
      props = {
        href: `/tags/show?tag=${tag}`,
        as
      };
    } else if (AUTHOR_REGEX.test(pathname)) {
      // Author URL
      const [, author] = pathname.match(AUTHOR_REGEX);
      props = {
        href: `/authors/show?author=${author}`,
        as
      };
    } else if (SEARCH_REGEX.test(pathname)) {
      // Search URL
      props = {
        href: url,
        as
      };
    } else if (IMAGE_COLLECTION_REGEX.test(pathname)) {
      // Image Collection URL
      props = {
        href: url,
        as
      };
    } else {
      // Unhandled internal URL
      props = {
        href: pathname
      };
    }
  }

  return props;
};

/**
 * returns root-relative path from given site URL
 *
 * @export
 * @param {String} [canonical=''] canonical URL
 * @returns {String} root-relative path from canonical URL
 */
export function getPathFromCanonical(canonical = '') {
  const url = toUrl(canonical);
  return url ? url.pathname : '';
}

/**
 * returns provided path without any fragment or query params.
 *
 * @export
 * @param {String} [path=''] path
 * @returns {String} path without query params or fragment ID
 */
export function getCleanPath(path = '') {
  const [cleanedPath] = path.split(/[?#]/);
  return cleanedPath;
}

export function getCategoryPath(category) {
  return `/${category.slug}`;
}

export function getTagPath(tag) {
  return `/collection/${tag.slug}`;
}

export function getKickerProps(tags, whitelist, categories) {
  // Filter using the whitelist (if provided)
  const safeTags = whitelist ? filterTermsBySlug(tags, whitelist.subtitle) : [];
  const [tag] = safeTags;
  const tagLinkProps = tag ? getLinkProps(getTagPath(tag)) : undefined;

  const [category] = whitelist
    ? filterTermsBySlug(categories, whitelist.categories)
    : [];
  const categoryLinkProps = category
    ? getLinkProps(getCategoryPath(category))
    : undefined;

  return {
    tag,
    tagLinkProps,
    category,
    categoryLinkProps
  };
}

export function getPostIdFromUrl(pathOrUrl) {
  const path = /^https?:\/\//.test(pathOrUrl)
    ? toUrl(pathOrUrl).pathname
    : pathOrUrl;

  const matches = path ? POST_REGEX.exec(path) : null;

  return matches && matches.length > 0 ? matches[1] : null;
}
